import { DriverHeader } from '@fluidframework/driver-definitions';
import { authRequestWithRetry } from '@fluidframework/odsp-doclib-utils';
import { mfsError } from '../errors/loaderErrors';
import { Result } from '@metaos/mfs-client';
import { sha256 } from 'js-sha256';
import { logger } from '../logger';
import { logVerbose, logVerboseAsync } from '@metaos/mfs-telemetry';
import { default as nodeFetch } from 'node-fetch';
import { ErrorCategory } from '../mfs/mfsService';
const fetch = async (request, config) => nodeFetch(request, config);
// There is no clear official domain name rules, but multiple sources agree on the following rules that this regex
// validates:
// - Domain label cannot start or end with a hyphen
// - Domain can have multiple labels of size 1 to 63
// - Domain label supports alphanumeric characters and hyphens
// - Domain labels are separated by a period
// - The top level domain must end with alphabetic characters and be between 2 and 6 characters
// Additionally, internationalized domain names (IDNA) can support Unicode characters that this regex does not take into
// account, but this should be OK since SharePoint domain names do not accept Unicode characters, and IDNA names also
// have an ASCII only form (called Punycode).
// updated to remove negative look behind. Both Babylon for native and Safari browser < v16.4 do not support this regex feature.
export const validDomainRegex = new RegExp(/^((?!-)[A-Za-z0-9-]{1,62}((?!-)[A-Za-z0-9])\.)+[A-Za-z]{2,6}$/);
/** Check if the provided URL is a valid path for creating/retrieving the Pod in ODSP */
export function isValidOdspUrl(odspUrl) {
    return logVerbose(logger, 'isValidOdspUrl', () => {
        let isValid = false;
        try {
            new URL(odspUrl);
            isValid = true;
        }
        catch (error) {
            // do nothing
        }
        logger.logVerbose('isValidOdspUrl', 'Returns', { odspUrl, isValid });
        return isValid;
    });
}
/** Checks if a domain is valid. */
export function isValidDomain(domain) {
    return logVerbose(logger, 'isValidDomain', () => {
        return validDomainRegex.test(domain);
    });
}
/** Generate the request object for creating an ODSP container. */
export function generateOdspCreateContainerRequest(pathFolderUrl, fileName, driveId) {
    // Use our own util function as the util provided by odsp-driver has bug that it will double encode the file path
    return logVerbose(logger, 'generateOdspCreateContainerRequest', () => {
        const request = {
            url: `${pathFolderUrl.origin}?driveId=${encodeURIComponent(driveId)}&path=${pathFolderUrl.pathname}`,
            headers: {
                [DriverHeader.createNew]: {
                    fileName,
                },
            },
        };
        logger.logVerbose('generateOdspCreateContainerRequest', 'Returning Request', { driveId });
        return request;
    });
}
/** Util function to get the DriveItem from the path */
export async function getDriveItem(domain, filePath, getOdspTokenFn, refreshOdspTokenFn, driveId) {
    return logVerboseAsync(logger, 'getDriveItem', async () => {
        const encodedDrive = encodeURIComponent(driveId);
        const url = `https://${domain}/_api/v2.1/drives/${encodedDrive}/root:${filePath}:`;
        return getDriveItemFromUrl(domain, url, getOdspTokenFn, refreshOdspTokenFn);
    });
}
/** Util function to get the default pod DriveItem */
export async function getDriveItemFromUrl(domain, url, getOdspTokenFn, refreshOdspTokenFn) {
    return logVerboseAsync(logger, 'getDriveItemFromUrl', async () => {
        const authRequestInfo = {
            accessToken: await getOdspTokenFn(domain),
            refreshTokenFn: () => refreshOdspTokenFn(domain),
        };
        const response = await getAsync(url, authRequestInfo, 'OpenFile_MFS');
        if (response.status === 404) {
            throw mfsError.fileNotFound();
        }
        else if (response.status !== 200) {
            throw mfsError.cannotGetDriveItemFromUrl(url);
        }
        const getDriveItemResult = await response.json();
        return toIODSPDriveItem(getDriveItemResult);
    });
}
/** Util function to convert JSON response to DriveItem. Duplicate of non exported Fluid Framework function. */
function toIODSPDriveItem(parsedDriveItemBody) {
    return logVerbose(logger, 'toIODSPDriveItem', () => {
        ensureCorrectDriveItemInfo(parsedDriveItemBody);
        const path = parsedDriveItemBody.parentReference.path
            ? parsedDriveItemBody.parentReference.path.split('root:')[1]
            : '/';
        return {
            path,
            name: parsedDriveItemBody.name,
            driveId: parsedDriveItemBody.parentReference.driveId,
            itemId: parsedDriveItemBody.id,
            isFolder: !!parsedDriveItemBody.folder,
        };
    });
}
function ensureCorrectDriveItemInfo(parsedDriveItemBody) {
    const invalidItems = [];
    if (!parsedDriveItemBody) {
        throw mfsError.invalidDriveItemInfo('drive item response');
    }
    if (!parsedDriveItemBody.id) {
        invalidItems.push('itemId');
    }
    if (!parsedDriveItemBody.parentReference || !parsedDriveItemBody.parentReference.driveId) {
        invalidItems.push('driveId');
    }
    if (invalidItems.length > 0) {
        throw mfsError.invalidDriveItemInfo(invalidItems);
    }
}
/**
 * Util function that will return the ODSP Apps Special folder. ODSP Special folders are predefined locations that are
 * used to store data of various types in central locations in a user's OneDrive.
 *
 * @param domain User's ODSP domain where their ODB is located.
 * @param getOdspTokenFn Callback function to retrieve an odsp token.
 * @param refreshOdspTokenFn Callback function to refresh odsp token.
 * @returns The ODSP Apps Special folder as an ODSP Drive Item.
 */
export async function getAppsSpecialFolderAsDriveItem(domain, getOdspTokenFn, refreshOdspTokenFn) {
    return logVerboseAsync(logger, 'getAppsSpecialFolderAsDriveItem', async () => {
        const url = `https://${domain}/_api/v2.1/drive/special/apps`;
        return getDriveItemFromUrl(domain, url, getOdspTokenFn, refreshOdspTokenFn);
    });
}
/** Util function to trim the last character of a string if it matches the character to remove */
function trimChar(input, charToRemove) {
    return logVerbose(logger, 'trimChar', () => {
        if (!input || input.length == 0) {
            logger.logVerbose('trimChar', 'Input is undefined or has length 0');
            return input;
        }
        if (input.charAt(input.length - 1) == charToRemove) {
            input = input.substring(0, input.length - 1);
        }
        logger.logVerbose('trimChar', 'Returning updated input');
        return input;
    });
}
/** Util function to get the drive item from a sharing URL */
export async function getDriveItemFromSharingURL(domain, sharingUrl, getOdspTokenFn, refreshOdspTokenFn) {
    return logVerboseAsync(logger, 'getDriveItemFromSharingURL', async () => {
        const authRequestInfo = {
            accessToken: await getOdspTokenFn(domain),
            refreshTokenFn: () => refreshOdspTokenFn(domain),
        };
        const base64Val = Buffer.from(sharingUrl).toString('base64');
        const encodedUrl = 'u!' + trimChar(base64Val, '=').replace('/', '_').replace('+', '-');
        const getDriveItemUrl = `https://${domain}/_api/v2.0/shares/${encodedUrl}/driveItem`;
        logger.logVerbose('getDriveItemFromSharingURL', `Getting drive item from url`);
        const response = await getAsync(getDriveItemUrl, authRequestInfo, 'OpenFile_MFS');
        const getDriveItemResult = await response.json();
        if (!getDriveItemResult || !getDriveItemResult.parentReference) {
            throw mfsError.invalidSharingURL(sharingUrl);
        }
        const driveAndItem = { driveId: getDriveItemResult.parentReference.driveId, itemId: getDriveItemResult.id };
        logger.logVerbose('getDriveItemFromSharingURL', `itemId: ${getDriveItemResult.id}`);
        return driveAndItem;
    });
}
/** Util function to get the default driveId for given domain */
export async function getDriveIdForDomain(domain, getOdspTokenFn, refreshOdspTokenFn) {
    return logVerbose(logger, 'getDriveIdForDomain', async () => {
        const authRequestInfo = {
            accessToken: await getOdspTokenFn(domain),
            refreshTokenFn: () => refreshOdspTokenFn(domain),
        };
        const url = `https://${domain}/_api/v2.1/drive?select=id`;
        const response = await getAsync(url, authRequestInfo, 'OpenFile_MFS');
        if (response.status !== 200) {
            throw mfsError.cannotGetDriveId(url);
        }
        const responseBody = await response.json();
        if (!responseBody || !responseBody.id) {
            throw mfsError.cannotGetDriveId(url);
        }
        return responseBody.id;
    });
}
/** Util function to get all Pod files from given folder path */
export async function getAllPodFilesWithinFolder(domain, folderPath, getOdspTokenFn, refreshOdspTokenFn, driveId) {
    return logVerbose(logger, 'getAllPodFilesWithinFolder', async () => {
        logger.logVerbose('getAllPodFilesWithinFolder', 'Getting folder', { domain, folderPath, driveId });
        const driveItem = await getDriveItem(domain, folderPath, getOdspTokenFn, refreshOdspTokenFn, driveId);
        if (!driveItem.isFolder) {
            throw mfsError.driveItemInvalidFolder();
        }
        logger.logVerbose('getAllPodFilesWithinFolder', 'Getting children', {
            parentId: driveItem.itemId,
            isFolder: driveItem.isFolder,
        });
        const childItems = await getChildrenByDriveItem(driveItem, domain, {
            accessToken: await getOdspTokenFn(domain),
            refreshTokenFn: () => refreshOdspTokenFn(domain),
        });
        const podFiles = childItems.filter((i) => i.name.endsWith('.pod'));
        logger.logVerbose('getAllPodFilesWithinFolder', `Found ${podFiles.length} files`, {
            podFiles: podFiles.map(({ driveId, itemId, isFolder }) => ({ driveId, itemId, isFolder })),
        });
        return podFiles;
    });
}
async function getChildrenByDriveItem(driveItem, domain, authRequestInfo) {
    return logVerbose(logger, 'getChildrenByDriveItem', async () => {
        if (!driveItem.isFolder) {
            return [];
        }
        let url = `https://${domain}/_api/v2.1/drives/${driveItem.driveId}/items/${driveItem.itemId}/children`;
        let children = [];
        do {
            const response = await getAsync(url, authRequestInfo, 'OpenFile_MFS');
            if (response.status !== 200) {
                throw mfsError.cannotGetDriveItemChildren(url);
            }
            const getChildrenResult = await response.json();
            children = children.concat(getChildrenResult.value);
            url = getChildrenResult['@odata.nextLink'];
        } while (url);
        return children.map(toIODSPDriveItem);
    });
}
/** Helper function to get the hash of the user's app ID */
export function getAppIdHash(appId) {
    return logVerbose(logger, 'getAppIdHash', () => {
        const hash = sha256(appId);
        logger.logVerbose('getAppIdHash', 'generated', { hash });
        return hash;
    });
}
/** Helper function to determine if the pod file name is suffixed with the app ID */
export function podFileNameHasExpectedSuffix(podFileName, expectedSuffix) {
    return logVerbose(logger, 'podFileNameHasExpectedSuffix', () => {
        // if truncated, check for equality
        if (!podFileName || !podFileName.includes(expectedSuffix)) {
            logger.logVerbose('podFileNameHasExpectedSuffix', 'Failed', {
                podFileName,
                expectedSuffix,
            });
            throw mfsError.podFileNameHasIncorrectSuffix();
        }
    });
}
/** Util function to get the ODSP driveItem object from given driveId and itemId */
export async function getDriveItemFromDriveItemId(domain, driveId, driveItemId, getOdspTokenFn, refreshOdspTokenFn) {
    return logVerboseAsync(logger, 'getDriveItemFromDriveItemId', async () => {
        logger.logVerbose('getDriveItemFromDriveItemId', 'Getting drive item', { domain, driveItemId, driveId });
        const url = `https://${domain}/_api/v2.1/drives/${driveId}/items/${driveItemId}`;
        return getDriveItemFromUrl(domain, url, getOdspTokenFn, refreshOdspTokenFn);
    });
}
/** Whether `array` is a homogeneously typed array of `T`. */
function isArrayOfType(array, 
// eslint-disable-next-line @typescript-eslint/no-explicit-any
typeGuard) {
    return logVerbose(logger, 'isArrayOfType', () => {
        return array.length > 0 && !array.some((a) => !typeGuard(a));
    });
}
/** Typeguard function for OdspFileVersionsResponse */
function isOdspFileVersionsResponse(input) {
    return logVerbose(logger, 'isOdspFileVersionsResponse', () => {
        return (typeof input === 'object' &&
            input !== null &&
            'value' in input &&
            isArrayOfType(input === null || input === void 0 ? void 0 : input.value, isOdspFileVersion));
    });
}
/** Typeguard function for OdspFileVersion */
function isOdspFileVersion(input) {
    return logVerbose(logger, 'isOdspFileVersion', () => {
        return typeof input === 'object' && input !== null && 'id' in input && 'lastModifiedDateTime' in input;
    });
}
/** Typeguard for ErrorCategory */
export function isErrorCategory(errorCode) {
    return Object.values(ErrorCategory).includes(errorCode);
}
/** Get file versions sorted by date (descending) */
export async function getFileVersions(domain, driveId, itemId, getOdspTokenFn, refreshOdspTokenFn) {
    return logVerboseAsync(logger, 'getFileVersions', async () => {
        const versionsUrl = `https://${domain}/_api/v2.0/drives/${driveId}/items/${itemId}/versions`;
        logger.logVerbose('getFileVersions', 'Enter', {
            domain,
            driveId,
            itemId,
            versionsUrl: 'https://###/_api/v2.0/drives/###/items/${itemId}/versions',
        });
        const authRequestInfo = {
            accessToken: await getOdspTokenFn(domain),
            refreshTokenFn: () => refreshOdspTokenFn(domain),
        };
        const response = await getAsync(versionsUrl, authRequestInfo, 'RestoreFile_MFS');
        if (response.status !== 200) {
            logger.logVerbose('getFileVersions', 'Exit - request failed', {
                statusCode: response.status,
                domain,
                driveId,
                itemId,
            });
            return Result.fail(new Error('Error getting file versions'));
        }
        const responseObject = await response.json();
        if (isOdspFileVersionsResponse(responseObject)) {
            // Sort OdspFileVersion objects by date in descending order
            const versions = responseObject.value.sort((a, b) => Date.parse(b.lastModifiedDateTime) - Date.parse(a.lastModifiedDateTime));
            logger.logVerbose('getFileVersions', 'Success', { versions });
            return Result.ok(versions);
        }
        else {
            logger.logVerbose('getFileVersions', 'Exit - unexpected response object', { responseObject });
            return Result.fail(new Error('Failed parsing get file versions response'));
        }
    });
}
export async function restoreFileVersion(domain, driveId, itemId, fileVersion, getOdspTokenFn, refreshOdspTokenFn) {
    return logVerboseAsync(logger, 'restoreFileVersion', async () => {
        if (!fileVersion) {
            return Result.fail(new Error('Invalid versionId'));
        }
        const authRequestInfo = {
            accessToken: await getOdspTokenFn(domain),
            refreshTokenFn: () => refreshOdspTokenFn(domain),
        };
        const restoreFileVersionUrl = `https://${domain}/_api/v2.0/drives/${driveId}/items/${itemId}/versions/${fileVersion.id}/restoreVersion`;
        logger.logVerbose('restoreFileVersion', 'Begin restore', {
            versionUrl: 'https://###/_api/v2.0/drives/###/items/${itemId}/versions/${fileVersion.id}/restoreVersion',
        });
        const response = await postAsync(restoreFileVersionUrl, '', authRequestInfo, 'RestoreFile_MFS');
        if (response.status !== 204) {
            logger.logVerbose('restoreFileVersion', 'Fail', { response });
            return Result.fail(new Error('Failure restoring file version'));
        }
        logger.logVerbose('restoreFileVersion', 'Success', { fileVersion });
        return Result.ok();
    });
}
async function getAsync(url, authRequestInfo, scenarioName) {
    return authRequest(authRequestInfo, scenarioName, async (config) => fetch(url, config));
}
async function postAsync(url, body, authRequestInfo, scenarioName) {
    return authRequest(authRequestInfo, scenarioName, async (config) => {
        const postConfig = {
            ...config,
            body,
            method: 'POST',
        };
        return fetch(url, postConfig);
    });
}
async function authRequest(authRequestInfo, scenarioName, requestCallback) {
    return authRequestWithRetry(authRequestInfo, async (config) => {
        const odspConfig = {
            ...config,
            headers: {
                ...config.headers,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                Application: 'MFSData',
                // eslint-disable-next-line @typescript-eslint/naming-convention
                Scenario: scenarioName,
                // eslint-disable-next-line @typescript-eslint/naming-convention
                ScenarioType: 'AUO',
            },
        };
        return safeRequestCore(async () => requestCallback(odspConfig));
    });
}
async function safeRequestCore(requestCallback) {
    var _a;
    let response;
    try {
        response = await requestCallback();
    }
    catch (error) {
        if ((_a = error === null || error === void 0 ? void 0 : error.response) === null || _a === void 0 ? void 0 : _a.status) {
            response = error.response;
        }
        else {
            throw error;
        }
    }
    return response;
}
//# sourceMappingURL=odspUtils.js.map